/**
 * settings の型 SystemSettings・NumberingRule（02 5.3.21）。更新 API は⑦で作る。
 */
import { z } from 'zod';
import { DATE_STYLE_TYPE_VALUES } from '../enums/date-style-type';
import { NUMBERING_TARGET_VALUES } from '../enums/numbering-target';
import { SEQUENCE_RESET_TYPE_VALUES } from '../enums/sequence-reset-type';
import { YEAR_FORMAT_VALUES } from '../enums/year-format';
import { zEmail, zEnum, zPhone, zPostalCode, zRequiredText, zShortText, zVersion } from './common.schema';
import { CommonAttributesSchema } from './list-query.schema';

export const SYSTEM_SETTING_KEY = 'company';

export const SystemSettingsSchema = z
  .object({
    setting_key: z.literal(SYSTEM_SETTING_KEY),
    own_company_name: z.string(),
    postal_code: z.string().nullable(),
    address: z.string().nullable(),
    phone_number: z.string().nullable(),
    email: z.string().nullable(),
    sender_display_name: z.string().nullable(),
  })
  .merge(CommonAttributesSchema);

export const SystemSettingsUpdateSchema = z
  .object({
    own_company_name: zRequiredText(200, 'MSG-V-047'),
    postal_code: zPostalCode.nullable().optional(),
    address: zShortText(500).optional(),
    phone_number: zPhone.nullable().optional(),
    email: zEmail.nullable().optional(),
    sender_display_name: zShortText(100).optional(),
    version: zVersion,
  })
  .strict();

const zTypeCodeDigit = z.string().regex(/^[0-9]$/, 'MSG-V-020');

export const NumberingRuleSchema = z
  .object({
    numbering_target: z.enum(NUMBERING_TARGET_VALUES),
    type_code_map: z.record(z.string()),
    year_format: z.enum(YEAR_FORMAT_VALUES),
    date_style_type: z.enum(DATE_STYLE_TYPE_VALUES),
    sequence_digits: z.number().int(),
    is_prefix_enabled: z.boolean(),
    prefix_text: z.string().nullable(),
    sequence_reset_type: z.enum(SEQUENCE_RESET_TYPE_VALUES),
    next_code_preview: z.union([z.string(), z.record(z.string())]),
  })
  .merge(CommonAttributesSchema);

export const NumberingRuleUpdateSchema = z
  .object({
    type_code_map: z
      .object({ company: zTypeCodeDigit, soleProprietor: zTypeCodeDigit })
      .strict()
      .refine((map) => map.company !== map.soleProprietor, 'MSG-V-020')
      .optional(),
    year_format: zEnum(YEAR_FORMAT_VALUES),
    date_style_type: zEnum(DATE_STYLE_TYPE_VALUES),
    sequence_digits: z.number().int().min(3).max(10),
    is_prefix_enabled: z.boolean(),
    prefix_text: z
      .string()
      .regex(/^[A-Z0-9-]{1,10}$/, 'MSG-V-020')
      .nullable()
      .optional(),
    sequence_reset_type: zEnum(SEQUENCE_RESET_TYPE_VALUES),
    version: zVersion,
  })
  .strict()
  .refine((value) => !value.is_prefix_enabled || Boolean(value.prefix_text), {
    message: 'MSG-V-020',
    path: ['prefix_text'],
  });

export type SystemSettings = z.infer<typeof SystemSettingsSchema>;
export type SystemSettingsUpdateInput = z.infer<typeof SystemSettingsUpdateSchema>;
export type NumberingRule = z.infer<typeof NumberingRuleSchema>;
export type NumberingRuleUpdateInput = z.infer<typeof NumberingRuleUpdateSchema>;
